import { useState } from 'react'
import { adminRequest } from './EshopCategories'

/** Spôsob doručenia, kontakt a adresa v detaile objednávky (+ doplnenie ceny dopravy „naceníme“). */
export type DeliveryOrder = {
  id: string
  customerName: string
  email?: string | null
  phone?: string | null
  note?: string | null
  deliveryMethod?: string
  deliveryAddress?: string | null
  deliveryMunicipality?: string | null
  deliveryPostalCode?: string | null
  deliveryPriceCents?: number
  deliveryPricePending?: boolean
  paymentStatus?: string
  status: string
  totalCents: number
  store?: { name: string }
  storeId?: string
  seasonalRuleId?: string | null
  paymentMethod?: string
}
type StoreOption = { id: string; name: string; active?: boolean }

export const DELIVERY_LABEL: Record<string, string> = { PICKUP: '🏠 Osobný odber', COURIER: '🚗 Doručenie na adresu', SHIPPING: '📦 Kuriér – balík' }
export const deliveryShort = (method?: string, storeName?: string) => method === 'COURIER' ? '🚗 Na adresu' : method === 'SHIPPING' ? '📦 Kuriér' : `🏠 ${storeName ?? 'Osobný odber'}`

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })

export default function OrderDelivery({ order, stores = [], onUpdated }: { order: DeliveryOrder; stores?: StoreOption[]; onUpdated: (patch: Partial<DeliveryOrder>) => void }) {
  const [changing, setChanging] = useState(false)
  const [price, setPrice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const method = order.deliveryMethod ?? 'PICKUP'
  const delivery = method !== 'PICKUP'
  const address = [order.deliveryAddress, [order.deliveryPostalCode, order.deliveryMunicipality].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  const canEditPrice = delivery && !['PAID', 'PENDING'].includes(order.paymentStatus ?? '') && !['CANCELLED', 'REJECTED', 'PICKED_UP', 'DELIVERED'].includes(order.status)

  async function savePrice() {
    const cents = Math.round(Number(price.replace(',', '.')) * 100)
    if (!price.trim() || !Number.isFinite(cents) || cents < 0) { setError('Zadajte cenu dopravy, napr. 4,50.'); return }
    setBusy(true); setError('')
    try {
      const data = await adminRequest(`/api/admin/orders/${order.id}/delivery-price`, { method: 'POST', body: JSON.stringify({ priceCents: cents }) })
      onUpdated({ deliveryPriceCents: data.deliveryPriceCents, deliveryPricePending: data.deliveryPricePending, totalCents: data.totalCents })
      setPrice('')
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie ceny sa nepodarilo.') } finally { setBusy(false) }
  }

  const mapUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : ''
  return <section className="od-box" aria-label="Doručenie a kontakt">
    <div className="od-head">
      <h3>{DELIVERY_LABEL[method] ?? method}{method === 'PICKUP' && order.store?.name ? ` – ${order.store.name}` : ''}</h3>
      {!changing && !['CANCELLED', 'REJECTED', 'PICKED_UP', 'DELIVERED'].includes(order.status) && <button type="button" className="pa-ghost" onClick={() => setChanging(true)}>⇄ Zmeniť doručenie / prevádzku</button>}
    </div>
    {changing && <DeliveryChange order={order} stores={stores} onCancel={() => setChanging(false)} onSaved={patch => { onUpdated(patch); setChanging(false) }} />}
    <dl className="od-grid">
      <div><dt>Zákazník</dt><dd>{order.customerName}</dd></div>
      {order.phone && <div><dt>Telefón</dt><dd><a href={`tel:${order.phone.replace(/\s+/g, '')}`}>{order.phone}</a></dd></div>}
      {order.email && <div><dt>E-mail</dt><dd><a href={`mailto:${order.email}`}>{order.email}</a></dd></div>}
      {(delivery || order.deliveryAddress) && <div className="od-wide"><dt>{delivery ? 'Adresa doručenia' : 'Adresa'}</dt><dd>{address || '—'}{mapUrl && <> · <a href={mapUrl} target="_blank" rel="noopener">mapa ↗</a></>}</dd></div>}
      {delivery && <div><dt>Doprava</dt><dd>{order.deliveryPricePending ? <span className="or-pill tone-warn">Treba naceniť</span> : euro(order.deliveryPriceCents ?? 0)}</dd></div>}
      {order.note && <div className="od-wide"><dt>Poznámka zákazníka</dt><dd>{order.note}</dd></div>}
    </dl>
    {canEditPrice && <div className="od-price">
      <label>{order.deliveryPricePending ? 'Cena dopravy (doplniť)' : 'Zmeniť cenu dopravy'}<span className="dl-price-input"><input inputMode="decimal" placeholder="napr. 4,50" value={price} onChange={e => setPrice(e.target.value)} /><span>€</span></span></label>
      <button type="button" className={order.deliveryPricePending ? 'primary-btn' : 'pa-ghost'} disabled={busy || !price.trim()} onClick={() => void savePrice()}>{busy ? 'Ukladám…' : 'Uložiť cenu'}</button>
      {order.deliveryPricePending && <small>Po uložení sa suma objednávky prepočíta. Zákazníkovi cenu potvrďte telefonicky alebo e-mailom.</small>}
    </div>}
    {error && <p className="error-message" role="alert">{error}</p>}
  </section>
}

/** Zmena spôsobu doručenia, prevádzky alebo adresy – keď sa zákazník rozhodne inak. */
function DeliveryChange({ order, stores, onCancel, onSaved }: { order: DeliveryOrder; stores: StoreOption[]; onCancel: () => void; onSaved: (patch: Partial<DeliveryOrder>) => void }) {
  const [method, setMethod] = useState(order.deliveryMethod ?? 'PICKUP')
  const [storeId, setStoreId] = useState(order.storeId ?? stores[0]?.id ?? '')
  const [municipality, setMunicipality] = useState(order.deliveryMunicipality ?? '')
  const [address, setAddress] = useState(order.deliveryAddress ?? '')
  const [postal, setPostal] = useState(order.deliveryPostalCode ?? '')
  const [price, setPrice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [warning, setWarning] = useState('')
  const seasonal = Boolean(order.seasonalRuleId)
  const paid = ['PAID', 'PENDING'].includes(order.paymentStatus ?? '')
  const activeStores = stores.filter(s => s.active !== false)

  async function save() {
    let priceCents: number | null = null
    if (method !== 'PICKUP' && price.trim()) {
      priceCents = Math.round(Number(price.replace(',', '.')) * 100)
      if (!Number.isFinite(priceCents) || priceCents < 0) { setError('Zadajte cenu dopravy, napr. 4,50 – alebo nechajte prázdne (podľa cenníka).'); return }
    }
    setBusy(true); setError('')
    try {
      const data = await adminRequest(`/api/admin/orders/${order.id}/delivery-change`, { method: 'POST', body: JSON.stringify({
        deliveryMethod: method,
        ...(method === 'PICKUP' ? { storeId } : { deliveryMunicipality: municipality.trim(), deliveryAddress: address.trim(), ...(method === 'SHIPPING' ? { deliveryPostalCode: postal.trim() } : {}) }),
        ...(method !== 'PICKUP' ? { deliveryPriceCents: priceCents } : {}),
      }) })
      const o = data.order
      if (data.warning) setWarning(data.warning)
      onSaved({ deliveryMethod: o.deliveryMethod, storeId: o.storeId, store: o.store, deliveryMunicipality: o.deliveryMunicipality, deliveryAddress: o.deliveryAddress, deliveryPostalCode: o.deliveryPostalCode, deliveryPriceCents: o.deliveryPriceCents, deliveryPricePending: o.deliveryPricePending, totalCents: o.totalCents, paymentMethod: o.paymentMethod })
    } catch (e) { setError(e instanceof Error ? e.message : 'Zmena sa nepodarila.') } finally { setBusy(false) }
  }

  const options: Array<[string, string, string]> = [['PICKUP', '🏠', 'Osobný odber'], ['COURIER', '🚗', 'Na adresu'], ['SHIPPING', '📦', 'Kuriér – balík']]
  return <div className="od-change">
    <div className="od-methods" role="radiogroup" aria-label="Spôsob doručenia">{options.map(([m, icon, label]) => <button key={m} type="button" role="radio" aria-checked={method === m} disabled={seasonal && m !== 'PICKUP'} className={method === m ? 'is-on' : ''} onClick={() => setMethod(m)}><span aria-hidden="true">{icon}</span>{label}</button>)}</div>
    {seasonal && <small className="od-hint">Vianočná objednávka je iba na osobný odber – zmeniť sa dá prevádzka.</small>}
    {method === 'PICKUP' ? <div className="od-stores" role="radiogroup" aria-label="Prevádzka">{activeStores.map(s => <button key={s.id} type="button" role="radio" aria-checked={storeId === s.id} className={storeId === s.id ? 'is-on' : ''} onClick={() => setStoreId(s.id)}>{s.name}</button>)}</div>
      : <div className="od-fields">
        <label className="od-wide">Ulica a číslo<input value={address} maxLength={200} onChange={e => setAddress(e.target.value)} /></label>
        {method === 'SHIPPING' && <label>PSČ<input value={postal} maxLength={6} inputMode="numeric" onChange={e => setPostal(e.target.value)} placeholder="971 01" /></label>}
        <label>{method === 'SHIPPING' ? 'Mesto / obec' : 'Obec'}<input value={municipality} maxLength={120} onChange={e => setMunicipality(e.target.value)} /></label>
        <label>Cena dopravy (€)<input inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} placeholder={method === 'SHIPPING' ? 'podľa cenníka' : 'podľa cenníka / naceniť'} /></label>
      </div>}
    {paid && <small className="od-hint">Objednávka je zaplatená online – cena dopravy musí zostať rovnaká ({euro(order.deliveryPriceCents ?? 0)}).</small>}
    {error && <p className="error-message" role="alert">{error}</p>}
    {warning && <p className="od-hint">{warning}</p>}
    <div className="od-actions"><button type="button" className="pa-ghost" onClick={onCancel}>Zrušiť</button><button type="button" className="primary-btn" disabled={busy || (method === 'PICKUP' && !storeId)} onClick={() => void save()}>{busy ? 'Ukladám…' : 'Uložiť zmenu'}</button></div>
  </div>
}
